/* eslint-disable jsx-a11y/no-autofocus */
import {
  AnimatePresence,
  HTMLMotionProps,
  motion,
  usePresence,
} from 'framer-motion';
import React, {
  ComponentPropsWithoutRef,
  FC,
  forwardRef,
  MouseEvent,
  ReactElement,
  ReactNode,
  RefObject,
  useEffect,
} from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import styled, { css } from 'styled-components';

import {
  __DEV__,
  callAllHandlers,
  createContext,
  cx,
  FocusableElement,
} from '~src/utils';

import { CloseButton, CloseButtonProps } from '../close-button';
import { FocusLock, FocusLockProps } from '../focus-lock';
import { Portal, PortalProps } from '../portal';
import { fadeConfig } from '../transition';
import { ModalTransition } from './modal-transition';
import { useModal, UseModalProps, UseModalReturn } from './use-modal';

interface ModalOptions extends Pick<FocusLockProps, 'lockFocusAcrossFrames'> {
  /**
   * If `false`, focus lock will be disabled completely.
   *
   * This is useful in situations where you still need to interact with
   * other surrounding elements.
   *
   * 🚨Warning: We don't recommend doing this because it hurts the
   * accessibility of the modal, based on WAI-ARIA specifications.
   *
   * @default true
   */
  trapFocus?: boolean;
  /**
   * If `true`, the modal will autofocus the first enabled and interactive
   * element within the `ModalContent`
   *
   * @default true
   */
  autoFocus?: boolean;
  /**
   * The `ref` of element to receive focus when the modal opens.
   */
  initialFocusRef?: RefObject<FocusableElement>;
  /**
   * The `ref` of element to receive focus when the modal closes.
   */
  finalFocusRef?: RefObject<FocusableElement>;
  /**
   * If `true`, the modal will return focus to the element that triggered it when it closes.
   * @default true
   */
  returnFocusOnClose?: boolean;
  /**
   * If `true`, scrolling will be disabled on the `body` when the modal opens.
   *  @default true
   */
  blockScrollOnMount?: boolean;
  /**
   * Handle zoom/pinch gestures on iOS devices when scroll locking is enabled.
   * Defaults to `false`.
   */
  allowPinchZoom?: boolean;
  /**
   * If `true`, a `padding-right` will be applied to the body element
   * that's equal to the width of the scrollbar.
   *
   * This can help prevent some unpleasant flickering effect
   * and content adjustment when the modal opens
   */
  preserveScrollBarGap?: boolean;
}

type ScrollBehavior = 'inside' | 'outside';

type MotionPreset = 'slideInBottom' | 'slideInRight' | 'scale';

export interface ModalProps extends UseModalProps, ModalOptions {
  children: ReactNode;
  /**
   *  If `true`, the modal will be centered on screen.
   * @default false
   */
  isCentered?: boolean;
  /**
   * Where scroll behavior should originate.
   * - If set to `inside`, scroll only occurs within the `ModalBody`.
   * - If set to `outside`, the entire `ModalContent` will scroll within the viewport.
   *
   * @default "outside"
   */
  scrollBehavior?: ScrollBehavior;
  /**
   * Function that will be called to get the parent element
   * that the modal will be attached to.
   */
  getContainer?: PortalProps['getContainer'];
  /**
   * The transition that should be used for the modal
   *
   * @default "scale"
   */
  motionPreset?: MotionPreset;
  /**
   * The width that should be used for the modal in vw
   *
   * @default "30"
   */
  size?: number;
  /**
   * the modal will occupy the viewport height and width (100vh, 100vw)
   *
   * @default "false"
   */
  isFull?: boolean;
  /**
   * If `true`, the modal will be centered on screen.
   * @default false
   */
  isRight?: boolean;
}

interface ModalContext extends ModalOptions, UseModalReturn {
  /**
   * If `true`, the modal will be centered on screen.
   * @default false
   */
  isCentered: boolean;
  /**
   * Where scroll behavior should originate.
   * - If set to `inside`, scroll only occurs within the `ModalBody`.
   * - If set to `outside`, the entire `ModalContent` will scroll within the viewport.
   *
   * @default "outside"
   */
  scrollBehavior: ScrollBehavior;
  /**
   * The transition that should be used for the modal
   *
   * @default "scale"
   */
  motionPreset: MotionPreset;
  /**
   * The width that should be used for the modal in vw
   *
   * @default "30"
   */
  size: number;
  /**
   * the modal will occupy the viewport height and width (100vh, 100vw)
   *
   * @default "false"
   */
  isFull: boolean;
  /**
   * If `true`, the modal will be centered on screen.
   * @default false
   */
  isRight: boolean;
}

const [ModalContextProvider, useModalContext] = createContext<ModalContext>({
  strict: true,
  name: 'ModalContext',
  errorMessage:
    'useModalContext: `context` is undefined. Seems you forgot to wrap modal components in `<Modal />`',
});

export { ModalContextProvider, useModalContext };

/**
 * Modal provides context, theming, and accessibility properties
 * to all other modal components.
 *
 * It doesn't render any DOM node.
 */
export const Modal: FC<ModalProps> = props => {
  const {
    getContainer,
    children,
    autoFocus = true,
    trapFocus = true,
    initialFocusRef,
    finalFocusRef,
    returnFocusOnClose = true,
    blockScrollOnMount = true,
    allowPinchZoom = false,
    preserveScrollBarGap,
    lockFocusAcrossFrames = false,
    motionPreset = 'scale',
    scrollBehavior = 'outside',
    isCentered = false,
    size = 30,
    isFull = false,
    isRight = false,
  } = props;

  const modal = useModal(props);

  const context = {
    ...modal,
    autoFocus,
    trapFocus,
    initialFocusRef,
    finalFocusRef,
    returnFocusOnClose,
    blockScrollOnMount,
    allowPinchZoom,
    preserveScrollBarGap,
    lockFocusAcrossFrames,
    motionPreset,
    isCentered,
    isFull,
    size: isFull ? 100 : size,
    scrollBehavior: isFull ? `outside` : scrollBehavior,
    isRight,
  };

  return (
    <ModalContextProvider value={context}>
      <AnimatePresence>
        {context.isOpen && (
          <Portal getContainer={getContainer}>{children}</Portal>
        )}
      </AnimatePresence>
    </ModalContextProvider>
  );
};

if (__DEV__) {
  Modal.displayName = 'Modal';
}

type DialogContainerProps = {
  scrollBehavior?: string;
  isCentered?: boolean;
  isRight?: boolean;
};

const DialogContainer = styled.div<DialogContainerProps>`
  display: flex;
  width: 100vw;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  z-index: ${p => p.theme.zIndexes.modal};
  justify-content: ${p => (p.isRight ? `flex-end` : `center`)};
  align-items: ${p => (p.isCentered ? `center` : `flex-start`)};
  overflow: ${p => (p.scrollBehavior === `inside` ? `hidden` : `auto`)};
`;

type DialogProps = {
  scrollBehavior?: string;
  size?: number;
  isFull?: boolean;
};

const Dialog = styled(ModalTransition)<DialogProps>`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  outline: 0;
  border-radius: ${p => (p.isFull ? 0 : ` 0.375rem`)};
  background: rgb(255, 255, 255);
  color: inherit;
  margin-top: ${p => (p.isFull ? 0 : `3.75rem`)};
  margin-bottom: ${p => (p.isFull ? 0 : `3.75rem`)};
  z-index: ${p => p.theme.zIndexes.modal};
  box-shadow: rgba(0, 0, 0, 0.1) 0 10px 15px -3px,
    rgba(0, 0, 0, 0.05) 0 4px 6px -2px;
  max-width: ${p => p.size}vw;
  ${p => p.scrollBehavior === `inside` && `max-height: calc(100vh - 7.5rem);`};
  ${p =>
    p.isFull &&
    css`
      height: 100vh;
      overflow: auto;
      max-height: 100%;
    `};
`;

export interface ModalContentProps {
  /**
   * The props to forward to the modal's content wrapper
   */
  containerProps?: ComponentPropsWithoutRef<'div'>;
  className?: string;
  role?: string;
  children: ReactNode;
}

/**
 * ModalContent is used to group modal's content. It has all the
 * necessary `aria-*` properties to indicate that it is a modal
 */
export const ModalContent = forwardRef<HTMLElement, ModalContentProps>(
  (props, ref) => {
    const { className, children, containerProps: rootProps, ...rest } = props;

    const {
      getDialogProps,
      getDialogContainerProps,
      scrollBehavior,
      isCentered,
      motionPreset,
      size,
      isFull,
      isRight,
    } = useModalContext();

    const dialogProps = getDialogProps(rest, ref);
    const containerProps = getDialogContainerProps(rootProps);

    const _className = cx('modal__content', className);

    return (
      <ModalFocusScope>
        <DialogContainer
          {...containerProps}
          className="modal__content-container"
          isCentered={isCentered}
          isRight={isRight}
        >
          <Dialog
            preset={motionPreset}
            scrollBehavior={scrollBehavior}
            className={_className}
            size={size}
            isFull={isFull}
            {...dialogProps}
          >
            {children}
          </Dialog>
        </DialogContainer>
      </ModalFocusScope>
    );
  }
);

if (__DEV__) {
  ModalContent.displayName = 'ModalContent';
}

interface ModalFocusScopeProps {
  /**
   * @type React.ReactElement
   */
  children: ReactElement;
}

export const ModalFocusScope: FC<ModalFocusScopeProps> = ({ children }) => {
  const {
    autoFocus,
    trapFocus,
    dialogRef,
    initialFocusRef,
    blockScrollOnMount,
    allowPinchZoom,
    finalFocusRef,
    returnFocusOnClose,
    preserveScrollBarGap,
  } = useModalContext();

  const [isPresent, safeToRemove] = usePresence();

  useEffect(() => {
    if (!isPresent && safeToRemove) {
      setTimeout(safeToRemove);
    }
  }, [isPresent, safeToRemove]);

  return (
    <FocusLock
      autoFocus={autoFocus}
      isDisabled={!trapFocus}
      initialFocusRef={initialFocusRef}
      finalFocusRef={finalFocusRef}
      restoreFocus={returnFocusOnClose}
      contentRef={dialogRef}
    >
      <RemoveScroll
        removeScrollBar={!preserveScrollBarGap}
        allowPinchZoom={allowPinchZoom}
        enabled={blockScrollOnMount}
        forwardProps
      >
        {children}
      </RemoveScroll>
    </FocusLock>
  );
};

const Overlay = styled(motion.div)`
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.48);
  z-index: ${p => p.theme.zIndexes.modal};
`;

export interface ModalOverlayProps
  extends Omit<HTMLMotionProps<'div'>, 'color'> {
  children?: ReactNode;
}

/**
 * ModalOverlay renders a backdrop behind the modal. It is
 * also used as a wrapper for the modal content for better positioning.
 *
 */
export const ModalOverlay = forwardRef<HTMLDivElement, ModalOverlayProps>(
  (props, ref) => {
    const { className, ...rest } = props;
    const _className = cx('modal__overlay', className);

    return (
      <Overlay {...fadeConfig} ref={ref} className={_className} {...rest} />
    );
  }
);

if (__DEV__) {
  ModalOverlay.displayName = 'ModalOverlay';
}

const Header = styled.header`
  flex: 0 1 0%;
  padding: 1rem 1.5rem;
  font-size: 1.25rem;
  font-weight: 600;
`;

export type ModalHeaderProps = {
  className?: string;
  children: ReactNode;
};

/**
 * ModalHeader
 *
 * React component that houses the title of the modal.
 *
 */
export const ModalHeader = forwardRef<HTMLElement, ModalHeaderProps>(
  (props, ref) => {
    const { className, ...rest } = props;

    const { headerId, setHeaderMounted } = useModalContext();

    /**
     * Notify us if this component was rendered or used
     * so we can append `aria-labelledby` automatically
     */
    useEffect(() => {
      setHeaderMounted(true);
      return () => setHeaderMounted(false);
    }, [setHeaderMounted]);

    const _className = cx('modal__header', className);

    return <Header ref={ref} className={_className} id={headerId} {...rest} />;
  }
);

if (__DEV__) {
  ModalHeader.displayName = 'ModalHeader';
}

const Body = styled.div<{ scrollBehavior?: string }>`
  padding: 0.5rem 1.5rem;
  flex: 1 1 0%;
  ${p => p.scrollBehavior === `inside` && `overflow: auto;`}
`;

export type ModalBodyProps = {
  className?: string;
  children: ReactNode;
};

/**
 * ModalBody
 *
 * React component that houses the main content of the modal.
 *
 */
export const ModalBody = forwardRef<HTMLDivElement, ModalBodyProps>(
  (props, ref) => {
    const { className, ...rest } = props;
    const { bodyId, setBodyMounted, scrollBehavior } = useModalContext();

    /**
     * Notify us if this component was rendered or used
     * so we can append `aria-describedby` automatically
     */
    useEffect(() => {
      setBodyMounted(true);
      return () => setBodyMounted(false);
    }, [setBodyMounted]);

    const _className = cx('modal__body', className);

    return (
      <Body
        ref={ref}
        className={_className}
        id={bodyId}
        scrollBehavior={scrollBehavior}
        {...rest}
      />
    );
  }
);

if (__DEV__) {
  ModalBody.displayName = 'ModalBody';
}

const Footer = styled.footer`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1em 1.5em;
`;

export type ModalFooterProps = {
  className?: string;
  children: ReactNode;
};

/**
 * ModalFooter houses the action buttons of the modal.
 */
export const ModalFooter = forwardRef<HTMLElement, ModalFooterProps>(
  (props, ref) => {
    const { className, ...rest } = props;
    const _className = cx('modal__footer', className);

    return <Footer ref={ref} {...rest} className={_className} />;
  }
);

if (__DEV__) {
  ModalFooter.displayName = 'ModalFooter';
}

const Close = styled(CloseButton)<{ noBorder: boolean }>`
  position: absolute;
  top: ${p => (p.noBorder ? `1.5em` : `3em`)};
  right: ${p => (p.noBorder ? `2.5em` : `4em`)};
`;

export type ModalCloseButtonProps = CloseButtonProps & {
  noBorder: boolean;
};

/**
 * ModalCloseButton is used closes the modal.
 *
 * You don't need to pass the `onClick` to it, it reads the
 * `onClose` action from the modal context.
 */
export const ModalCloseButton = forwardRef<
  HTMLButtonElement,
  ModalCloseButtonProps
>((props, ref) => {
  const { onClick, className, noBorder, ...rest } = props;
  const { onClose } = useModalContext();

  const _className = cx('modal__close-btn', className);

  return (
    <Close
      ref={ref}
      className={_className}
      noBorder={noBorder}
      onClick={callAllHandlers(onClick, (event: MouseEvent) => {
        event.stopPropagation();
        onClose();
      })}
      {...rest}
    />
  );
});

if (__DEV__) {
  ModalCloseButton.displayName = 'ModalCloseButton';
}
